<template>
  <form class="loginDiv" method="post" @submit.prevent="handleSubmit">
    <div class="background-image-div" :style="{ backgroundImage: 'url(' + require('@/assets/images/vasion-logo-texture.png') + ')'}">
      <div class="innerLoginDiv">
        <div class="w100 icon-fill">
          <VasionLogoWhite class="vasion-wordmark" />
        </div>
        <div class="loginSpacer32" />
        
        <div class="two-factor-header">
          <h2 class="two-factor-title">Two-Factor Authentication</h2>
          <p class="two-factor-subtitle">Enter the verification code sent to your email</p>
        </div>
        
        <div class="loginSpacer24" />
        
        <label class="loginLabelStyle">VERIFICATION CODE</label>
        <input
          id="twoFactorCode"
          v-model="twoFactorCode"
          class="loginTextBox"
          :class="{ loginTextBoxError: twoFactorError }"
          type="text"
          placeholder="Enter 6-digit code"
          maxlength="6"
          pattern="[0-9]{6}"
          required
          autocomplete="off"
          @input="clearTwoFactorError"
        />
        
        <div class="loginSpacer24" />
        
        <div class="login-button-div">
          <VasionButton
            id="two-factor-submit"
            :isFullWidth="true"
            :is-submit="true"
            :classProp="'primary'"
            :isDisabled="invalidTwoFactorParams"
          >
            <span v-if="!isLoading">Verify</span>
            <div v-else id="loader-box">
              <Loading
                :active.sync="isLoading"
                :is-full-page="false"
                loader="dots"
                :color="'white'"
                :background-color="'#3D2562'"
              />
            </div>
          </VasionButton>
          
          <div class="loginSpacer24" />
          
          <VasionButton
            id="resend-code-button"
            :isFullWidth="true"
            :classProp="isResending ? 'secondary' : 'primary'"
            :isDisabled="isResending"
            @vasionButtonClicked="resendCode"
          >
            <span v-if="!isResending">Resend Code</span>
            <div v-else id="loader-box">
              <Loading
                :active.sync="isResending"
                :is-full-page="false"
                loader="dots"
                :color="'white'"
                :background-color="'#ff4702'"
              />
            </div>
          </VasionButton>
          
          <div id="back-to-login-button" @click="goBackToLogin">
            ← Back to Login
          </div>
          
          <h4 v-if="twoFactorError" id="invalid-code-notification">
            <VasionCircleError class="vasion-circle-error" />
            <div class="error-login">
              {{ showTwoFactorError }}
            </div>
          </h4>
        </div>
      </div>
      
      <VasionSnackbar
        id="two-factor-snack"
        :showSnackbarBool.sync="showSnackbarBool"
        :snackbarImage="snackbarImage"
        :snackbarSubTitle="snackbarSubTitle"
        :snackbarTitle="snackbarTitle"
      />

      <VasionMSAModal
        id="vasion-msa-modal"
        :showModal="showMSA"
        @yesButtonClick="msaModalYes"
        @noButtonClick="msaModalNo"
      />
    </div>
  </form>
</template>

<script>
import Loading from 'vue-loading-overlay';
import { loaderBackgroundColor, loaderColor } from '@/assets/js/styleConfig'
import VasionMSAModal from '@/components/shared/VasionMSAModal.vue'

export default {
  name: 'TwoFactorAuth',
  components: {
    Loading,
    VasionMSAModal,
  },
  data: function () {
    return {
      twoFactorCode: '',
      twoFactorError: false,
      isLoading: false,
      isResending: false,
      resendTimer: null,
      loaderBackgroundColor,
      loaderColor,
      showMSA: false,
      showSnackbarBool: false,
      snackbarImage: '',
      snackbarSubTitle: '',
      snackbarTitle: '',
      twoFactorData: null, // Store the two-factor auth data
    }
  },
  computed: {
    invalidTwoFactorParams() { 
      return !this.twoFactorCode || this.twoFactorCode.length !== 6 
    },
    showTwoFactorError() { 
      return this.$store.state.common.twoFactorError ? this.$store.state.common.twoFactorError : "Invalid verification code." 
    },
    backgroundUrl() {
      // eslint-disable-next-line
      return require('@/assets/images/vasion-logo-texture.png')
    },
  },
  created: function () {
    // Get the credentials from the route params or store
    this.credentials = this.$route.params.credentials || this.$store.state.common.pendingCredentials
    
    // If no credentials are available, redirect back to login
    if (!this.credentials) {
      this.$router.push({ name: 'TheLogin' })
    }
    
  },
  beforeDestroy() {
    if (this.resendTimer) {
      clearInterval(this.resendTimer)
    }
  },
  methods: {
    async handleSubmit() {
      this.isLoading = true
      this.twoFactorError = false

      if (this.invalidTwoFactorParams) {
        this.isLoading = false
        return
      }

      try {
        // Call the two-factor authentication API
        const payload = {
          ...this.$store.state.common.pendingCredentials,
          iUserID: this.$store.state.common.loginUserId,
          sMachineID: 'WEB',
          sAuthCode: this.twoFactorCode
        }

        await this.$store.dispatch('common/verifyTwoFactorCode', payload)
        if (this.$store.state.common.isPortalMSAApproved === false && this.$store.state.common.isPortalUser) {
          this.twoFactorError = true
          this.showMSA = true
        }
        else if (this.$store.state.common.isSignedIn === true) {
          this.routeFromSuccessfulLogin()
          this.$store.dispatch('common/setPendingCredentials', null) // Clear pending credentials after successful login
        } else {
          this.twoFactorError = true
        }
      } catch (error) {
        console.error('Two-factor authentication error:', error)
        this.twoFactorError = true
      }

      this.isLoading = false
    },
    async msaModalNo()
    {
      this.twoFactorError = true
      this.showMSA = false
      await this.$store.dispatch('common/signOut')
    },
    async msaModalYes()
    {
      this.showMSA = false
      this.routeFromSuccessfulLogin()
      this.$store.dispatch('common/setPendingCredentials', null) // Clear pending credentials after successful login
    },    
    async resendCode() {
      if (this.isResending) {
        return
      }

      this.isResending = true

      try {
        // Call the resend two-factor code API
        const payload = {
          iUserID: this.$store.state.common.loginUserId,
          sMachineID: 'WEB',
        }

        const success = await this.$store.dispatch('common/resendTwoFactorCode', payload)
        
        if (success) {
          this.snackbarTitle = 'Code Sent'
          this.snackbarSubTitle = 'A new verification code has been sent to your device.'
          this.snackbarImage = true
          this.showSnackbarBool = true
        } else {
          this.snackbarTitle = 'Error'
          this.snackbarSubTitle = 'Failed to resend verification code. Please try again.'
          this.snackbarImage = false
          this.showSnackbarBool = true
        }
      } catch (error) {
        console.error('Resend code error:', error)
        this.snackbarTitle = 'Error'
        this.snackbarSubTitle = 'Failed to resend verification code. Please try again.'
        this.snackbarImage = false
        this.showSnackbarBool = true
      }

      this.isResending = false
    },
    
    clearTwoFactorError() {
      this.twoFactorError = false
    },
    
    goBackToLogin() {
      // Clear any stored credentials and go back to login
      this.$store.dispatch('common/clearPendingCredentials')
      this.$router.push({ name: 'TheLogin' })
    },
    
    async routeFromSuccessfulLogin() {
      // Use the same routing logic as the original login
      await this.$store.dispatch('admin/getAllUserSettings')
      await this.$store.dispatch('workflow/setCustomFolderView', this.$store.state.workflow.customFolderViewSaved)

      // Check for redirect first, before checking user type
      if (this.$route && this.$route.query && this.$route.query.redirect && this.$route.query.redirect !== '' && this.$route.query.redirect !== '/') {
        this.isLoading = false
        if (this.$route.query.redirect.indexOf('/document/') > -1) {
          this.$router.push({ path: "storage/vaults", query: { redirect: this.$route.query.redirect } })
        } else {
          this.$router.push({ path: this.$route.query.redirect })
        }
        return
      }

      // If no redirect, check if portal user and route to default portal page
      const isPortalUser = localStorage.getItem('IsPortalUser') === 'true'
      if (isPortalUser) {
        this.$router.push({ name: 'PortalTasksPending' })
        return
      }

      // Use the same routing logic from TheLogin.vue
      const defaultApp = this.$store.state.admin.defaultApp
      
      if (defaultApp && defaultApp !== '') {
        this.isLoading = false
        this.$router.push({ name: this.getDefaultAppLocation(defaultApp) })
      } else {
        this.isLoading = false
        const firstApp = this.getFirstAccessibleAppLocation()
        if (firstApp) {
          this.$router.push({ name: firstApp })
        } else {
          this.$router.push({ name: 'TheVasionHome' })
        }
      }
    },
    
    getDefaultAppLocation(defaultApp) {
      switch (defaultApp) {
        case 'admin':
          return 'TheEmptyAdmin'
        case 'capture':
          return 'StartScan'
        case 'signature':
          this.$store.dispatch('mainViews/changeMainNav', 'signatureInbox')
          return 'TheSignatureInbox'
        case 'reports':
          return 'TheEmptyReport'
        case 'storage':
          return 'VaultFolders'
        case 'workspace':
          this.$store.dispatch('mainViews/changeMainNav', 'my-tasks-pending')
          return 'TheWorkspaceHome'
        case 'forms':
          return 'AllForms'
        case 'templates':
          this.$store.dispatch('common/getEmailTemplates')
          this.$store.dispatch('mainViews/changeMainNav', 'all-emails')
          return 'AllEmailTemplates'
        case 'automation':
          return 'TheAutomationHome'
        case 'workflow':
          this.$store.dispatch('workflow/selectWorkflow', 0)
          this.$store.dispatch('mainViews/changeMainNav', 'workflow-designer')
          return 'AllWorkflows'
        default:
          this.$store.dispatch('mainViews/changeMainNav', 'my-tasks-pending')
          return 'TheWorkspaceHome'
      }
    },
    
    getFirstAccessibleAppLocation() {
      if (this.$store.getters['common/APP_ACCESS_WORKSPACE']) {
        this.$store.dispatch('mainViews/changeMainNav', 'my-tasks-pending')
        return 'TheWorkspaceHome'
      } else if (this.$store.getters['common/APP_ACCESS_REPORTS']) {
        return 'TheEmptyReport'
      } else if (this.$store.getters['common/APP_ACCESS_STORAGE']) {
        return 'VaultFolders'
      } else if (this.$store.getters['common/APP_ACCESS_CAPTURE']) {
        return 'StartScan'
      } else if (this.$store.getters['common/APP_ACCESS_FORMS']) {
        return 'AllForms'
      } else if (this.$store.getters['common/APP_ACCESS_SIGNATURE']) {
        this.$store.dispatch('mainViews/changeMainNav', 'signatureInbox')
        return 'TheSignatureInbox'
      } else if (this.$store.getters['common/APP_ACCESS_WORKFLOW']) {
        this.$store.dispatch('workflow/selectWorkflow', 0)
        this.$store.dispatch('mainViews/changeMainNav', 'workflow-designer')
        return 'AllWorkflows'
      } else if (this.$store.getters['common/APP_ACCESS_TEMPLATES']) {
        this.$store.dispatch('common/getEmailTemplates')
        this.$store.dispatch('mainViews/changeMainNav', 'all-emails')
        return 'AllEmailTemplates'
      } else if (this.$store.getters['common/APP_ACCESS_AUTOMATION']) {
        return 'TheAutomationHome'
      } else if (this.$store.getters['common/APP_ACCESS_ADMIN']) {
        return 'TheEmptyAdmin'
      }
      return null
    },
  },
}
</script>

<style lang="scss" scoped>
@import '@/assets/css/variables.scss';
@import '@/assets/css/light-theme-system-palette.scss';

.background-image-div {
  height: 100%;
  width: 100%;
  background-size: cover;
  background-repeat: no-repeat;
  display: flex;
  align-items: center;
  justify-content: center;
}

.icon-fill {
  fill: $orange-300;
}

.innerLoginDiv {
  width: 85%;
  max-width: 420px;
}

.two-factor-header {
  text-align: center;
  margin-bottom: 24px;
}

.two-factor-title {
  color: $white;
  font-family: $font-family-book;
  font-size: 24px;
  font-weight: 500;
  margin: 0 0 8px 0;
}

.two-factor-subtitle {
  color: $grey-300;
  font-family: $font-family-book;
  font-size: 14px;
  margin: 0;
}

#back-to-login-button {
  color: $grey-300;
  text-align: center;
  cursor: pointer;
  margin-top: 16px;
  font-size: 14px;
}

#invalid-code-notification {
  display: flex;
  align-items: center;
  justify-content: center;
  color: $error;
  fill: $error;
  font-size: 17px;

  .vasion-circle-error {
    margin-right: 3px;
    padding-top: 2px;
  }
}

.error-login {
  max-width: 400px;
}

#loader-box {
  min-height: 36px;
  min-width: 419px;
  z-index: 30;
}

.login-button-div {
  position: relative;
  left: -7px;
  width: 103.5%;
}

.loginDiv {
  width: calc(100vw);
  background-color: #252626;
  height: calc(100vh);
}

input:-webkit-autofill,
input:-webkit-autofill:hover,
input:-webkit-autofill:focus,
input:-webkit-autofill:active {
   transition: all 0s 50000s;
}

.loginSpacer24 {
  height: 24px;
}

.loginSpacer32 {
  height: 32px;
}

.loginTextBox {
  width: 100%;
  color: $grey-300;
  background-color: rgba(0, 0, 0, 0.2);
  border-width: 0px;
  border-radius: 4px;
  height: 40px;
  font-size: 16px;
  padding: 10px;
  -webkit-box-sizing: border-box;
  -moz-box-sizing: border-box;
  box-sizing: border-box;
}

.loginTextBoxError {
  border-width: 1px;
  border-color: red;
}

.loginLabelStyle {
  font-family: Gotham Book;
  font-size: 12px;
  font-weight: 500;
  font-style: normal;
  font-stretch: normal;
  line-height: normal;
  letter-spacing: normal;
  color: #aab8c1;
}

.vasion-wordmark {
  width: 100%;
  position: relative;
  right: 8px;
}

.w100 {
  width: 100%;
  text-align: center;
}

::v-deep .vasion-secondary {
  border: solid 1px $white !important;
  color: $black !important;
  background-color: $white !important;

  &:hover, &:focus {
    background-color: $btn-secondary-hover-bg !important;
  }
}
</style>
